import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';

import DocumentViewer from './viewers/DocumentViewer';

// A specific content type is more trustworthy than the filename extension, which
// can lie (e.g. a PDF renamed .txt). Prefer a known MIME → type mapping; fall back
// to the extension only when the content type is generic/absent.
const MIME_TO_TYPE: Record<string, string> = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/msword': 'doc',
  'text/plain': 'txt',
};

const fileTypeOf = (fileName?: string | null, contentType?: string | null) => {
  const ct = contentType?.split(';')[0].trim().toLowerCase();
  if (ct && MIME_TO_TYPE[ct]) return MIME_TO_TYPE[ct];
  if (ct?.startsWith('image/')) return ct.split('/').pop();
  return fileName?.split('.').pop()?.toLowerCase() || (ct ? ct.split('/').pop() : undefined);
};

interface Props {
  // Identity of the file to show. When it changes (and is truthy) the file is
  // (re)fetched; falsy renders nothing.
  sourceKey: string | null | undefined;
  // Fetch the file's bytes for the current sourceKey. Held in a ref, so passing
  // an inline arrow doesn't retrigger the fetch on every render.
  fetcher: () => Promise<Blob>;
  fileName?: string | null;
  contentType?: string | null;
  // Notified of the current object URL (and null on clear/error) — e.g. so a
  // caller can offer a Download link for the loaded blob.
  onBlobUrl?: (url: string | null) => void;
  style?: React.CSSProperties;
}

// BlobDocViewer is the single place that turns an authenticated file fetch into
// an inline preview. It owns the whole blob lifecycle (fetch → object URL →
// revoke) and the loading / error / empty states, so every caller — applicant
// documents, file uploads, invoice attachments, … — gets identical behavior,
// including a real message instead of a blank pane when the fetch fails.
const BlobDocViewer: React.FC<Props> = ({ sourceKey, fetcher, fileName, contentType, onBlobUrl, style }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const onBlobUrlRef = useRef(onBlobUrl);
  onBlobUrlRef.current = onBlobUrl;

  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    setBlobUrl(null);
    setError(null);
    onBlobUrlRef.current?.(null);
    if (!sourceKey) return;
    setLoading(true);
    fetcherRef.current()
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob as Blob);
        setBlobUrl(url);
        onBlobUrlRef.current?.(url);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setError(e?.response?.status === 404 ? 'File not found.' : 'Failed to load file.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [sourceKey]);

  const documents = useMemo(
    () => (blobUrl ? [{ uri: blobUrl, fileType: fileTypeOf(fileName, contentType) }] : []),
    [blobUrl, fileName, contentType]
  );

  if (!sourceKey) return null;
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5" style={style}>
        <Spinner animation="border" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center p-5 text-muted" style={style}>
        <i className="bi bi-exclamation-triangle fs-1 mb-2" />
        <div>{error}</div>
      </div>
    );
  }
  if (blobUrl) return <DocumentViewer documents={documents} style={style} />;
  return null;
};

export default BlobDocViewer;
